import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Divider from "antd/es/divider";
import Popover from "antd/es/popover";
import Space from "antd/es/space";
import isEqual from "lodash-es/isEqual";
import { defineMessages, useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Button } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import { getActiveEntityType, getEditedLayoutDefaultCheckInVariant } from "../../store";
import { setDefaultCheckInVariant } from "../../store/actions";
import DefaultCheckInVariant from "../../store/DefaultCheckInVariant";

import DefaultCheckInVariantSelect from "./DefaultCheckInVariantSelect";
import styles from "./EditDefaultCheckInVariant.module.scss";

const messages = defineMessages({
  cancelButtonTitle: {
    id: "settings.formLayouts.modal.variants.defaultCheckInVariantEdit.cancelButtonTitle",
    defaultMessage: "Cancel",
    description: "Cancel check-in default variant open button title",
  },
  description: {
    id: "settings.formLayouts.modal.variants.defaultCheckInVariantEdit.description",
    defaultMessage:
      "If variants exist, you can choose which one to display as the default for users performing check-ins with this layout.",
    description: "Set check-in default variant description",
  },
  openButtonTitle: {
    id: "settings.formLayouts.modal.variants.defaultCheckInVariantEdit.openButtonTitle",
    defaultMessage: "Set check-in default variant",
    description: "Set check-in default variant cancel button title",
  },
  saveButtonTitle: {
    id: "settings.formLayouts.modal.variants.defaultCheckInVariantEdit.saveButtonTitle",
    defaultMessage: "Save",
    description: "Save check-in default save button title",
  },
  title: {
    id: "settings.formLayouts.modal.variants.defaultCheckInVariantEdit.title",
    defaultMessage: "Set Check-in Default Variant",
    description: "Set check-in default variant title",
  },
});

interface EditDefaultCheckInVariantActionProps {
  defaultCheckInVariant?: ReturnType<typeof getEditedLayoutDefaultCheckInVariant>;
  entityType: ReturnType<typeof getActiveEntityType>;
  onDefaultCheckInVariantChange: typeof setDefaultCheckInVariant;
}

const EditDefaultCheckInVariantAction: FC<EditDefaultCheckInVariantActionProps> = ({
  defaultCheckInVariant,
  entityType,
  onDefaultCheckInVariantChange,
}) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);

  const [newDefaultCheckinVariant, setNewDefaultCheckinVariant] = useState<
    DefaultCheckInVariant | undefined
  >();

  useEffect(() => {
    setNewDefaultCheckinVariant(defaultCheckInVariant);
  }, [defaultCheckInVariant, setNewDefaultCheckinVariant]);

  const saveEnabled = useMemo(
    () => !isEqual(defaultCheckInVariant, newDefaultCheckinVariant),
    [defaultCheckInVariant, newDefaultCheckinVariant]
  );

  const handleChange = useCallback(
    (value?: DefaultCheckInVariant) => {
      setNewDefaultCheckinVariant(value);
    },
    [setNewDefaultCheckinVariant]
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleSave = useCallback(() => {
    onDefaultCheckInVariantChange(newDefaultCheckinVariant);
    setOpen(false);
  }, [newDefaultCheckinVariant, onDefaultCheckInVariantChange, setOpen]);

  if (entityType !== EntityType.ACTIVITY) {
    return null;
  }

  return (
    <Popover
      content={
        <div className={styles.contentContainer}>
          <div className={styles.contentTitle}>{intl.formatMessage(messages.title)}</div>
          <div className={styles.contentDescription}>
            {intl.formatMessage(messages.description)}
          </div>
          <Divider className={styles.contentDivider} />
          <DefaultCheckInVariantSelect onChange={handleChange} value={newDefaultCheckinVariant} />
          <Space className={styles.contentActions}>
            <Button onClick={handleCancel}>{intl.formatMessage(messages.cancelButtonTitle)}</Button>
            <Button disabled={!saveEnabled} onClick={handleSave} type="primary">
              {intl.formatMessage(messages.saveButtonTitle)}
            </Button>
          </Space>
        </div>
      }
      destroyTooltipOnHide
      onOpenChange={setOpen}
      open={open}
      overlayClassName={styles.popoverOverlay}
      placement="bottomRight"
      trigger={["click"]}
    >
      <Button
        className={styles.openBtn}
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        type="link"
      >
        {intl.formatMessage(messages.openButtonTitle)}
      </Button>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  defaultCheckInVariant: getEditedLayoutDefaultCheckInVariant(state),
  entityType: getActiveEntityType(state),
});

const mapDispatchToProps = {
  onDefaultCheckInVariantChange: setDefaultCheckInVariant,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditDefaultCheckInVariantAction);
